'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Subscription {
  id: string;
  productName: string;
  productImage: string;
  productAlt: string;
  quantity: number;
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  nextDelivery: string;
  price: number;
  status: 'active' | 'paused';
}

interface SubscriptionManagementSectionProps {
  subscriptions: Subscription[];
}

const SubscriptionManagementSection = ({ subscriptions: initialSubscriptions }: SubscriptionManagementSectionProps) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>(initialSubscriptions);

  const handleToggleStatus = (id: string) => {
    setSubscriptions(
      subscriptions.map((sub) =>
        sub.id === id
          ? { ...sub, status: sub.status === 'active' ? 'paused' : 'active' }
          : sub
      )
    );
  };

  const handleCancel = (id: string) => {
    setSubscriptions(subscriptions.filter((sub) => sub.id !== id));
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return 'Every Day';
      case 'weekly':
        return 'Every Week';
      case 'biweekly':
        return 'Every 2 Weeks';
      case 'monthly':
        return 'Every Month';
      default:
        return frequency;
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="ArrowPathIcon" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-lg text-foreground">Active Subscriptions</h2>
            <p className="text-sm text-muted-foreground">{subscriptions.length} recurring orders</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {subscriptions.map((subscription) => (
          <div key={subscription.id} className="p-4 rounded-lg border border-border bg-background">
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-md overflow-hidden border border-border flex-shrink-0">
                <AppImage
                  src={subscription.productImage}
                  alt={subscription.productAlt}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex-1">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <h3 className="font-medium text-sm text-foreground mb-1">{subscription.productName}</h3>
                    <p className="text-xs text-muted-foreground">Quantity: {subscription.quantity} kg</p>
                  </div>
                  <div className="text-right">
                    <p className="font-data font-semibold text-base text-primary">
                      ₹{subscription.price.toLocaleString('en-IN')}
                    </p>
                    <p className="text-xs text-muted-foreground">per delivery</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <Icon name="CalendarIcon" size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{getFrequencyLabel(subscription.frequency)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="TruckIcon" size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Next: {subscription.nextDelivery}</span>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      subscription.status === 'active' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                    }`}
                  >
                    {subscription.status === 'active' ? 'Active' : 'Paused'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleStatus(subscription.id)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-muted text-foreground rounded-md font-medium text-xs transition-smooth hover:bg-border"
                  >
                    <Icon name={subscription.status === 'active' ? 'PauseIcon' : 'PlayIcon'} size={14} />
                    <span>{subscription.status === 'active' ? 'Pause' : 'Resume'}</span>
                  </button>
                  <button className="flex items-center gap-2 px-3 py-1.5 bg-muted text-foreground rounded-md font-medium text-xs transition-smooth hover:bg-border">
                    <Icon name="PencilIcon" size={14} />
                    <span>Modify</span>
                  </button>
                  <button
                    onClick={() => handleCancel(subscription.id)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-error/10 text-error rounded-md font-medium text-xs transition-smooth hover:bg-error/20"
                  >
                    <Icon name="XMarkIcon" size={14} />
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {subscriptions.length === 0 && (
          <div className="text-center py-12">
            <Icon name="ArrowPathIcon" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground mb-4">No active subscriptions</p>
            <button className="px-6 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift">
              Browse Products
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionManagementSection;